import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, ShieldAlert, Store, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * VendorListTable — Phase 11 (shared across 7 forms).
 * Renders on RequisitionDetail once the requisition exists. Access model:
 *   • PO / super_admin  → edit main columns + add rows (Status/Remarks locked)
 *   • HOS / super_admin → edit Status + Remarks by HOS (structural cols locked)
 *   • Applicant / others → read-only
 *
 * Column set is taken verbatim from the uploaded Vendor List xlsx:
 *   Sl.No, Purpose, Vendor Details, Price, Quote attachments,
 *   Status (access only for HOS), Remarks by only HOS
 */
const STATUS_OPTIONS = ["", "Approved", "On Hold", "Rejected"];
const MAX_ROWS = 20;

export default function VendorListTable({ requisition, currentUser, onUpdated }) {
  const initial = useMemo(() => {
    const rows = (requisition?.form_data?.vendor_list || []).map((r, i) => ({
      sl_no: r.sl_no || i + 1,
      purpose: r.purpose || "",
      vendor_details: r.vendor_details || "",
      price: r.price || "",
      quote_attachments: r.quote_attachments || "",
      status: r.status || "",
      remarks_by_hos: r.remarks_by_hos || "",
    }));
    while (rows.length < 3) {
      rows.push({ sl_no: rows.length + 1, purpose: "", vendor_details: "",
                   price: "", quote_attachments: "", status: "", remarks_by_hos: "" });
    }
    return rows;
  }, [requisition?.form_data?.vendor_list]);

  const [rows, setRows] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(initial); }, [initial]);

  const isSuper = currentUser?.role === "super_admin";
  const isHos = currentUser?.id === requisition?.admin2_id;
  const poIds = new Set([
    ...(requisition?.stores_po_ids || []),
    requisition?.transport_po_id,
    requisition?.fnb_po_id,
    requisition?.admin1_id,
    requisition?.alternate_admin1_id,
  ].filter(Boolean));
  const isPo = poIds.has(currentUser?.id);
  const canEditMain = isSuper || isPo;
  const canEditHos = isSuper || isHos;
  const readOnly = !canEditMain && !canEditHos;

  const updateCell = (i, col, value) => {
    setRows((prev) => prev.map((r, ix) => ix === i ? { ...r, [col]: value } : r));
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    setRows((prev) => [...prev, {
      sl_no: prev.length + 1, purpose: "", vendor_details: "",
      price: "", quote_attachments: "", status: "", remarks_by_hos: "",
    }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/requisitions/${requisition.ref_code}/vendor-list`,
        { rows },
      );
      toast.success("Vendor list saved.");
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatApiError(e), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const th = "px-2 py-2 text-left text-xs uppercase tracking-wider font-semibold text-[#4a524c] bg-[#f6efe0]";
  const td = "px-2 py-1 align-top border-b border-[#efe5cd] text-sm";

  return (
    <div className="sheet p-5 sm:p-7" data-testid="vendor-list-table">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Store size={18} className="text-[#7a1f2b]" />
          <h3 className="font-serif text-base font-semibold text-[#7a1f2b]">
            Vendor List
          </h3>
          <span className="text-[10px] uppercase tracking-widest text-[#6e7770]">
            (fill only if approx cost &gt; ₹9,999)
          </span>
        </div>
        <div className="text-[11px] flex items-center gap-2">
          {isHos && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f9e8ec] text-[#7a1f2b] border border-[#7a1f2b]/40"><ShieldAlert size={11}/>HOS · Status/Remarks</span>}
          {isPo && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#e8f2f2] text-[#2d6b6b] border border-[#2d6b6b]/40"><Store size={11}/>Procurement · Rows</span>}
          {readOnly && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#faf5eb] text-[#8a6a2f] border border-[#b48a45]/50"><Lock size={11}/>Read only</span>}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full">
          <thead>
            <tr>
              <th className={th}>Sl.No</th>
              <th className={th}>Purpose</th>
              <th className={th}>Vendor Details</th>
              <th className={th}>Price</th>
              <th className={th}>Quote attachments</th>
              <th className={th + " bg-[#f9e8ec] text-[#7a1f2b]"}>Status (HOS only)</th>
              <th className={th + " bg-[#f9e8ec] text-[#7a1f2b]"}>Remarks by only HOS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`vendor-row-${i}`}>
                <td className={td + " w-12 text-center text-[#6e7770]"}>{r.sl_no}</td>
                <td className={td}>
                  <input className="w-full input-vgws py-1 text-sm"
                          value={r.purpose}
                          onChange={(e) => updateCell(i, "purpose", e.target.value)}
                          disabled={!canEditMain}
                          data-testid={`vendor-cell-purpose-${i}`} />
                </td>
                <td className={td}>
                  <input className="w-full input-vgws py-1 text-sm"
                          value={r.vendor_details}
                          onChange={(e) => updateCell(i, "vendor_details", e.target.value)}
                          disabled={!canEditMain}
                          data-testid={`vendor-cell-vendor-${i}`} />
                </td>
                <td className={td}>
                  <input className="w-full input-vgws py-1 text-sm"
                          value={r.price}
                          onChange={(e) => updateCell(i, "price", e.target.value)}
                          disabled={!canEditMain}
                          data-testid={`vendor-cell-price-${i}`} />
                </td>
                <td className={td}>
                  <input className="w-full input-vgws py-1 text-sm"
                          value={r.quote_attachments}
                          placeholder="Link or file name"
                          onChange={(e) => updateCell(i, "quote_attachments", e.target.value)}
                          disabled={!canEditMain}
                          data-testid={`vendor-cell-quote-${i}`} />
                </td>
                <td className={td + " bg-[#fdf5f6]"}>
                  <select className="w-full input-vgws py-1 text-sm"
                           value={r.status}
                           onChange={(e) => updateCell(i, "status", e.target.value)}
                           disabled={!canEditHos}
                           data-testid={`vendor-cell-status-${i}`}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s || "—"}</option>
                    ))}
                  </select>
                </td>
                <td className={td + " bg-[#fdf5f6]"}>
                  <input className="w-full input-vgws py-1 text-sm"
                          value={r.remarks_by_hos}
                          onChange={(e) => updateCell(i, "remarks_by_hos", e.target.value)}
                          disabled={!canEditHos}
                          data-testid={`vendor-cell-remarks-${i}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="flex items-center gap-2 mt-3">
          {canEditMain && (
            <button className="btn-ghost text-xs" onClick={addRow}
                     disabled={rows.length >= MAX_ROWS}
                     data-testid="vendor-add-row">
              <Plus size={12} /> +1 row {rows.length >= MAX_ROWS && "(max)"}
            </button>
          )}
          <button className="btn-vgws text-xs" onClick={save} disabled={saving}
                   data-testid="vendor-save">
            {saving
              ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Save size={12} />}
            {saving ? "Saving…" : "Save vendor list"}
          </button>
        </div>
      )}
    </div>
  );
}
